import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';
import { butcherSubscriptionsStatus, errorMessage } from '@helpers/Constants';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import { getTrialDaysRemaining } from '@helpers/GetRemainingDays';
import { Subscription } from '@helpers/Models';
import { handleDateFormat } from '@services/OrderCreationService';
import { useState, useEffect } from 'react';

export const CancelTrial: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { showToastMessage } = useToast();
  const [subscriptionDetails, setSubscriptionDetails] =
    useState<Subscription | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscriptionDetails();
  }, []);

  /**
   * Cancels the current subscription.
   *
   * @async
   * @function cancelSubscription
   * @returns {Promise<void>}
   *
   * @remarks
   * - Sends a cancellation request to the backend along with user feedback.
   * - On success:
   *   - Displays a success toast message.
   *   - Updates the subscription status in `localStorage` to CANCELLED.
   *   - Closes the modal or dialog.
   *   - Reloads the page to reflect the updated subscription state.
   * - On failure:
   *   - Displays an error toast message.
   * - Manages loading state during the API call.
   */
  const cancelSubscription = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await Axios.post(ApiEndpoints.CANCEL_SUBSCRIPTION, {
        feedback: feedback,
      });
      showToastMessage(response.data.message, 'success');
      // Set the subscription status to cancelled (5)
      localStorage.setItem(
        'subscriptionStatus',
        butcherSubscriptionsStatus.CANCELLED.toString(),
      );
      onClose();
      // Do reload to get the latest subscription status reflected
      window.location.reload();
    } catch {
      showToastMessage(errorMessage.ERROR_CANCELLING_SUBSCRIPTION, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Fetches the current subscription details from the backend.
   *
   * @async
   * @function fetchSubscriptionDetails
   * @returns {Promise<void>}
   *
   * @remarks
   * - Retrieves subscription data using the API endpoint.
   * - Updates the local state with the fetched subscription details.
   * - Stores the latest subscription status in `localStorage` for global access.
   * - Displays an error toast message if the request fails.
   */
  const fetchSubscriptionDetails = async (): Promise<void> => {
    try {
      const response = await Axios.get(
        ApiEndpoints.FETCH_BUTCHER_SUBSCRIPTION_DETAILS,
      );
      setSubscriptionDetails(response.data.data.subscription);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const WhatYoullMissTexts = [
    'Advanced analytics dashboard',
    'Customer database',
    'Automated notifications',
    'Order Management',
    'Email support',
  ];

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-2xl max-h-[95vh] rounded-lg shadow-lg p-3">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex justify-between items-center px-2 py-4 border-b">
          <div className="flex items-center gap-2">
            <h2 className="text-2xl font-bold">Cancel Trial Period</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#1E40AF] font-bold text-xl leading-none"
          >
            ✕
          </button>
        </div>

        {/* Main Content */}
        <div className="overflow-y-auto">
          {/* Scrollable Body */}
          <div className="p-3 flex flex-col gap-5 max-h-[75vh] overflow-y-auto">
            {/* Trial Info Banner */}
            <div className="bg-[#EFF6FF] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#1E40AF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span className="text-[#1E40AF] font-bold text-[16px]">
                  You&apos;re Still in Your Trial Period
                </span>
              </div>
              <p className="text-[#1E3A8A] text-[17px] leading-relaxed mb-3">
                You can cancel your trial at any time without any charges.
                However, you&apos;ll lose access to all premium features
                immediately.
              </p>

              {/* Trial Period Remaining */}
              <div className="bg-white rounded-lg px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-[#6B7280] text-xs mb-0.5">
                    Trial Period Remaining
                  </p>
                  <p className="text-[#1E40AF] text-[18px] font-bold text-base">
                    {getTrialDaysRemaining(subscriptionDetails?.trialEndsAt)}{' '}
                    days left
                  </p>
                </div>
                <span className="bg-[#DBEAFE] text-[#1E40AF] font-semibold text-sm px-4 py-1.5 rounded-full">
                  Ends{' '}
                  {handleDateFormat(subscriptionDetails?.trialEndsAt ?? '')}
                </span>
              </div>
            </div>

            {/* Cancellation Reason */}
            <div>
              <label className="block text-gray-500 text-sm mb-2">
                Help us improve - Why are you canceling? *
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="The features don't match what I'm looking for..."
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
              <p className="text-[#6B7280] text-[14px] mt-1">
                Your feedback is valuable to us
              </p>
            </div>

            {/* No Charges Banner */}
            <div className="bg-[#F0FDF4] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.6 14.6L15.65 7.55L14.25 6.15L8.6 11.8L5.75 8.95L4.35 10.35L8.6 14.6ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18Z"
                    fill="#004834"
                  />
                </svg>
                <span className="text-[#166534] font-bold text-sm">
                  No Charges Applied
                </span>
              </div>
              <p className="text-[#15803D] text-sm leading-relaxed">
                Since you&apos;re canceling during the trial period, no charges
                will be applied to your account. You can reactivate your
                subscription anytime.
              </p>
            </div>

            {/* What You'll Miss */}
            <div className="border border-gray-200 rounded-xl p-4">
              <p className="text-gray-900 font-bold text-sm mb-3">
                What You&apos;ll Miss
              </p>
              <div className="flex flex-col gap-2.5">
                {subscriptionDetails?.promoCodes
                  ?.filter((promo) => promo.isActive == 1)
                  .map((promo, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#374151"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mt-0.5 flex-shrink-0"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      <span className="text-gray-700 text-sm">
                        {promo.PromoCodes.code} - {promo.PromoCodes.description}
                      </span>
                    </div>
                  ))}

                {WhatYoullMissTexts.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#374151"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mt-0.5 flex-shrink-0"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    <span className="text-gray-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            {/* Footer Buttons */}
            <div className="flex gap-3 py-2 border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex-1 border-2 border-primary text-primary bg-white font-semibold rounded-md text-sm py-3"
              >
                Keep My Trial
              </button>
              <button
                type="button"
                onClick={cancelSubscription}
                className="flex-1 bg-primary text-white font-semibold text-sm py-3 rounded-md"
              >
                Cancel Trial
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
